import { valueRender } from '@repo/toolkit/conditional-render'
import { isDefined } from '@repo/toolkit/is-defined'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { TFunction } from 'i18next'

import CountryFlag from '../../../components/elements/country-flag'
import PeriodBadge from '../../../components/elements/period-badge'
import TranslatedTypography from '../../../components/elements/translated-typography'
import { Paths } from '../../../lib/constants/path'
import { hostFamilyPriceTypeEnumMapping } from '../../../lib/types/enums/ol/host-family-price-type.enum'
import { HostFamily } from '../../../lib/types/models/host-family/host-family'
import {
  HostFamilyInput,
  HostFamilyRequest
} from '../../../lib/types/models/host-family/host-family-input'
import { OlCommission } from '../../../lib/types/models/ol/commission/ol-commission'
import { OLHostFamilyPricePerNight } from '../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night'
import { OlTeflPrice } from '../../../lib/types/models/ol/tefl-price/ol-tefl-price'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

export const constructFeePath = (path: string) => Paths.OL_FEES_LIST_PAGE.replace('*', '') + path

export const mapHostFamilyToInput = (hostFamily: HostFamily): HostFamilyInput => ({
  accomodationType: hostFamily.accomodationType,
  address1: hostFamily.address1,
  children: hostFamily.children.map(({ id: _id, ...child }) => ({ ...child })),
  city: hostFamily.city,
  country: hostFamily.country,
  deactivated: hostFamily.deactivated,
  createdAt: hostFamily.createdAt,
  familyName: hostFamily.familyName,
  hasPets: hostFamily.hasPets,
  hobbies: hostFamily.hobbies,
  withGarden: hostFamily.withGarden,
  isSharedRoom: hostFamily.isSharedRoom,
  maritalStatus: hostFamily.maritalStatus,
  numberOfBedrooms: hostFamily.numberOfBedrooms,
  parent1DateOfBirth: hostFamily.parent1DateOfBirth,
  parent1Email: hostFamily.parent1Email,
  parent1FirstName: hostFamily.parent1FirstName,
  parent1LastName: hostFamily.parent1LastName,
  parent1Nationality: hostFamily.parent1Nationality,
  parent1Position: hostFamily.parent1Position,
  parent1PositionStatus: hostFamily.parent1PositionStatus,
  parent2PositionStatus: hostFamily.parent2PositionStatus,
  relatives: hostFamily.relatives.map(({ id: _id, ...relative }) => ({ ...relative })),
  updatedAt: hostFamily.updatedAt,
  timeToSchool: hostFamily.timeToSchool,
  transportToSchool: hostFamily.transportToSchool,
  zipCode: hostFamily.zipCode,
  address2: hostFamily.address2,
  otherInformation: hostFamily.otherInformation,
  dateOfVisit: hostFamily.dateOfVisit,
  parent1PhoneNumber: hostFamily.parent1PhoneNumber,
  parent2DateOfBirth: hostFamily.parent2DateOfBirth,
  parent2Email: hostFamily.parent2Email,
  parent2FirstName: hostFamily.parent2FirstName,
  parent2LastName: hostFamily.parent2LastName,
  parent2Nationality: hostFamily.parent2Nationality,
  parent2PhoneNumber: hostFamily.parent2PhoneNumber,
  parent2Position: hostFamily.parent2Position,
  petDescription: hostFamily.petDescription,
  pickupPoint: hostFamily.pickupPoint,
  trainStation: hostFamily.trainStation,
  visitedBy: hostFamily.visitedBy,
  studentAccomodationComment: hostFamily.studentAccomodationComment
})

export const mapHostFamilyInputToRequest = ({
  createdAt: _createdAt,
  updatedAt: _updatedAt,
  deactivated: _deactivated,
  ...hostFamily
}: HostFamilyInput): HostFamilyRequest => hostFamily

type CommissionVersion = 'COMMISION' | 'FEES'

export const commissionsColumns: (
  t: TFunction<AvailableLanguages, undefined>
) => (Column<OlCommission> & { version: CommissionVersion[] })[] = (
  t: TFunction<AvailableLanguages, undefined>
) => [
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'products',
    header: t('commissions.countries.header'),
    cell: ({ row }) => {
      const products = row.original.products ?? []
      // get the countries from the products and remove duplicates
      const countries = Array.from(
        new Set(products.map((product) => product?.country?.code).filter(Boolean))
      )
      return (
        <div className="flex flex-row gap-1 p-1 items-center">
          {countries.map((country) => (
            <CountryFlag key={country} code={country} />
          ))}
        </div>
      )
    }
  },
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'products',
    header: t('commissions.formulas.header'),
    cell: ({ row }) => {
      const products = row.original.products ?? []

      const formula = products
        .map((product) => getTranslatedName(product.formula))
        .filter(Boolean)
        .join(', ')

      const formulaEn = products
        .map((product) => getTranslatedName(product.formula, 'en'))
        .filter(Boolean)
        .join(', ')

      return (
        <TranslatedTypography
          className="text-balance py-2"
          variant="secondary-body"
          keys={{ en: formulaEn, fr: formula }}
        />
      )
    }
  },
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'periods',
    header: t('commissions.periods.header'),
    cell: ({ row }) => (
      <div className={'flex flex-wrap  py-3 gap-1'}>
        {row.original.periods.map((period, index) => (
          <PeriodBadge key={index} period={period} />
        ))}
      </div>
    )
  },
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'durationsInDays',
    header: t('commissions.applied.durations.in.days.header'),
    cell: ({ row }) => {
      const durations = row.original.durationsInDays
      if (!durations.length) return '-'
      if (durations.length === 1) {
        return (
          <div className="flex flex-wrap gap-1 py-1">
            <Badge text={`${durations[0]}`} variant="grey" />
          </div>
        )
      }

      const min = Math.min(...durations)
      const max = Math.max(...durations)

      return (
        <div className="flex flex-wrap gap-1 py-1">
          <Badge text={`${min} ${t('to')} ${max}`} variant="grey" />
        </div>
      )
    }
  },
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.ol.fees.per.fa1ePrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa1ePrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['COMMISION', 'FEES'],
    accessorKey: 'fa2ePrice',
    header: t('commissions.ol.fees.per.fa2ePrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa2ePrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['COMMISION'],
    accessorKey: 'advancePayment',
    header: t('commissions.advancePayment.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.advancePayment, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['COMMISION'],
    accessorKey: 'retainer',
    header: t('commissions.retainer.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.retainer, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['COMMISION'],
    accessorKey: 'adminFeePerStudent',
    header: t('commissions.adminFeePerStudent.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.adminFeePerStudent, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  }
]

export const getCommissionColumns = (
  version: CommissionVersion,
  t: TFunction<AvailableLanguages, undefined>
): Column<OlCommission>[] => {
  // Map the input version to the internal CommissionVersion
  const columns = commissionsColumns(t)
  return columns.filter((column) => column.version.includes(version))
}

type HostFamilyPriceVersion = 'HOST_FAMILY' | 'FEES'

// a price has OTO pricing only when LEC filled the hourly fee or the weekly visit pack
export const hasOtoPrices = ({
  otoPerHourPrice,
  visitPackPerWeekPrice
}: OLHostFamilyPricePerNight) => isDefined(otoPerHourPrice) || isDefined(visitPackPerWeekPrice)

export const hostFamilyPricesColumns = (
  t: TFunction<AvailableLanguages, undefined>
): (Column<OLHostFamilyPricePerNight> & {
  version: HostFamilyPriceVersion[]
  isOto?: boolean
})[] => [
  {
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'appliedToHost',
    header: t('commissions.appliedToHost.header'),
    cell: ({ row }) => (
      <Badge
        text={t(hostFamilyPriceTypeEnumMapping[row.original.appliedToHost].label)}
        variant={hostFamilyPriceTypeEnumMapping[row.original.appliedToHost].variant}
      />
    )
  },
  {
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'products',
    header: t('commissions.formulas.header'),
    cell: ({ row }) => {
      const formula = row.original.products
        .map((product) => getTranslatedName(product.formula))
        .filter(Boolean)
        .join(', ')
      const formulaEn = row.original.products
        .map((product) => getTranslatedName(product.formula, 'en'))
        .filter(Boolean)
        .join(', ')
      return (
        <TranslatedTypography
          className="text-balance"
          variant="secondary-body"
          keys={{ en: formulaEn, fr: formula }}
        />
      )
    }
  },
  {
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'products',
    header: t('commissions.hours.per.week.header'),
    cell: ({ row }) => {
      // unique hours/week values across the products, ignoring missing ones
      const hours = Array.from(
        new Set(
          (row.original.products ?? [])
            .map((product) => product.numberOfHoursPerWeek)
            .filter((h): h is number => h !== undefined && h !== null)
        )
      ).sort((a, b) => a - b)

      if (!hours.length) return '-'

      return (
        <div className="flex flex-wrap gap-1 py-1">
          {hours.map((h) => (
            <Badge
              key={h}
              variant="white"
              text={t('product.number.of.hours.per.week', { hours: h })}
            />
          ))}
        </div>
      )
    }
  },
  {
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'periods',
    header: t('commissions.periods.header'),
    cell: ({ row }) => (
      <div className={'flex flex-wrap py-3 gap-1'}>
        {row.original.periods.map((period, index) => (
          <PeriodBadge key={index} period={period} />
        ))}
      </div>
    )
  },
  {
    version: ['HOST_FAMILY'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.fa1ePrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={NumberFormatter.formatCurrency(row.original.fa1ePrice ?? 0, row.original.currency)}
        />
      </div>
    )
  },
  {
    version: ['FEES'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.fa1ePrice.header'),
    enableSorting: true,
    columnHeaderClassName: 'border-l border-l-grey-light-hover',
    cellClassName: 'border-l border-l-grey-light-hover',
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa1ePrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['FEES'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.fa1ePrice.per.week.header'),
    enableSorting: true,
    columnHeaderClassName: 'border-r border-r-grey-light-hover',
    cellClassName: 'border-r border-r-grey-light-hover',
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa1ePrice, (v) =>
            NumberFormatter.formatCurrency(v * 7, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'fa2ePrice',
    header: t('commissions.fa2ePrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa2ePrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['FEES'],
    accessorKey: 'fa2ePrice',
    header: t('commissions.fa2ePrice.per.week.header'),
    enableSorting: true,
    columnHeaderClassName: 'border-r border-r-grey-light-hover',
    cellClassName: 'border-r border-r-grey-light-hover',
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.fa2ePrice, (v) =>
            NumberFormatter.formatCurrency(v * 7, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    version: ['HOST_FAMILY'],
    accessorKey: 'otoPerHourPrice',
    header: t('commissions.otoPerHourPrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.otoPerHourPrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    isOto: true,
    version: ['FEES'],
    accessorKey: 'otoPerHourPrice',
    header: t('commissions.otoPerHourPrice.header'),
    enableSorting: true,
    columnHeaderClassName: 'border-r border-r-grey-light-hover',
    cellClassName: 'border-r border-r-grey-light-hover',
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.otoPerHourPrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    isOto: true,
    version: ['HOST_FAMILY', 'FEES'],
    accessorKey: 'visitPackPerWeekPrice',
    header: t('commissions.visitPackPrice.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <Badge
          variant="darkBlue"
          text={valueRender(row.original.visitPackPerWeekPrice, (v) =>
            NumberFormatter.formatCurrency(v, row.original.currency)
          )}
        />
      </div>
    )
  },
  {
    isOto: true,
    version: ['FEES'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.fa1ePrice.10h.week.total.header'),
    enableSorting: false,
    cell: ({ row }) => {
      const { fa1ePrice, otoPerHourPrice, visitPackPerWeekPrice } = row.original
      const price =
        (fa1ePrice ?? 0) * 7 + (visitPackPerWeekPrice ?? 0) + (otoPerHourPrice ?? 0) * 10

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="darkBlue"
            text={
              hasOtoPrices(row.original)
                ? valueRender(fa1ePrice, () =>
                    NumberFormatter.formatCurrency(price, row.original.currency)
                  )
                : '-'
            }
          />
        </div>
      )
    }
  },
  {
    isOto: true,
    version: ['FEES'],
    accessorKey: 'fa2ePrice',
    header: t('commissions.fa2ePrice.10h.week.total.header'),
    enableSorting: false,
    cell: ({ row }) => {
      const { fa2ePrice, otoPerHourPrice, visitPackPerWeekPrice } = row.original
      const price =
        (fa2ePrice ?? 0) * 7 + (visitPackPerWeekPrice ?? 0) + (otoPerHourPrice ?? 0) * 10

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="darkBlue"
            text={
              hasOtoPrices(row.original)
                ? valueRender(fa2ePrice, () =>
                    NumberFormatter.formatCurrency(price, row.original.currency)
                  )
                : '-'
            }
          />
        </div>
      )
    }
  },
  {
    isOto: true,
    version: ['FEES'],
    accessorKey: 'fa1ePrice',
    header: t('commissions.fa1ePrice.15h.week.total.header'),
    enableSorting: false,
    cell: ({ row }) => {
      const { fa1ePrice, otoPerHourPrice, visitPackPerWeekPrice } = row.original
      const price =
        (fa1ePrice ?? 0) * 7 + (visitPackPerWeekPrice ?? 0) + (otoPerHourPrice ?? 0) * 15

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="darkBlue"
            text={
              hasOtoPrices(row.original)
                ? valueRender(fa1ePrice, () =>
                    NumberFormatter.formatCurrency(price, row.original.currency)
                  )
                : '-'
            }
          />
        </div>
      )
    }
  },
  {
    isOto: true,
    version: ['FEES'],
    accessorKey: 'fa2ePrice',
    header: t('commissions.fa2ePrice.15h.week.total.header'),
    enableSorting: false,
    cell: ({ row }) => {
      const { fa2ePrice, otoPerHourPrice, visitPackPerWeekPrice } = row.original
      const price =
        (fa2ePrice ?? 0) * 7 + (visitPackPerWeekPrice ?? 0) + (otoPerHourPrice ?? 0) * 15

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="darkBlue"
            text={
              hasOtoPrices(row.original)
                ? valueRender(fa2ePrice, () =>
                    NumberFormatter.formatCurrency(price, row.original.currency)
                  )
                : '-'
            }
          />
        </div>
      )
    }
  }
]

export const getHostFamilyPriceColumns = (
  version: HostFamilyPriceVersion,
  t: TFunction<AvailableLanguages, undefined>,
  showOto = true
): Column<OLHostFamilyPricePerNight>[] => {
  // Map the input version to the internal HostFamilyPriceVersion
  const columns = hostFamilyPricesColumns(t)
  return columns.filter((column) => column.version.includes(version) && (showOto || !column.isOto))
}

type TeflPriceVersion = 'TEFL' | 'FEES'

export const teflPricesColumns = (
  t: TFunction<AvailableLanguages, undefined>
): (Column<OlTeflPrice> & { version: TeflPriceVersion[] })[] => [
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'products',
    header: t('commissions.formulas.header'),
    cell: ({ row }) => {
      const products = row.original.products
      if (!products?.length) return '-'

      return (
        <div className="flex flex-col gap-2">
          {products.map((product, index) => {
            const formula = product.formula

            return (
              <div key={index} className="flex flex-row gap-2 items-center">
                <TranslatedTypography
                  variant="secondary-body"
                  keys={{ en: getTranslatedName(formula, 'en'), fr: getTranslatedName(formula) }}
                />

                <Badge
                  variant="white"
                  text={`${product.numberOfHoursPerDay} ${t('commissions.countries.hours.per.day')}`}
                />
              </div>
            )
          })}
        </div>
      )
    }
  },
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'periods',
    header: t('commissions.periods.header'),
    cell: ({ row }) => (
      <div className={'flex flex-wrap  py-3 gap-1'}>
        {row.original.periods.map((period, index) => (
          <PeriodBadge key={index} period={period} />
        ))}
      </div>
    )
  },
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'tuitionAndActivityPrice',
    header: t('commissions.tuitionAndActivity.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="darkBlue"
        text={valueRender(row.original.tuitionAndActivityPrice, (v) =>
          NumberFormatter.formatCurrency(v, row.original.currency)
        )}
      />
    )
  },
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'tuitionOnlyPrice',
    header: t('commissions.tuitionOnly.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="darkBlue"
        text={valueRender(row.original.tuitionOnlyPrice, (v) =>
          NumberFormatter.formatCurrency(v, row.original.currency)
        )}
      />
    )
  },
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'activityOnlyPrice',
    header: t('commissions.activityOnly.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="darkBlue"
        text={valueRender(row.original.activityOnlyPrice, (v) =>
          NumberFormatter.formatCurrency(v, row.original.currency)
        )}
      />
    )
  },
  {
    version: ['TEFL', 'FEES'],
    accessorKey: 'fullDayPrice',
    header: t('commissions.fullDayOuting.header'),
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        variant="darkBlue"
        text={valueRender(row.original.fullDayPrice, (v) =>
          NumberFormatter.formatCurrency(v, row.original.currency)
        )}
      />
    )
  }
]

export const getTeflPriceColumns = (
  version: TeflPriceVersion,
  t: TFunction<AvailableLanguages, undefined>
): Column<OlTeflPrice>[] => {
  // Map the input version to the internal TeflPriceVersion
  const columns = teflPricesColumns(t)
  return columns.filter((column) => column.version.includes(version))
}
